import styled from '@emotion/styled'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'

const StyledContainer = styled.div`
  ${({ $collapsed }) =>
    $collapsed
      ? 'height: 0; overflow: hidden;'
      : 'height: unset; overflow: unset;'}
`

const StyledInner = styled.div`
  display: flex;
  flex-direction: column;
`

const duration = 250

const getElementHeight = el => {
  const h = el.offsetHeight
  const style = window.getComputedStyle(el)

  const height = ['top', 'bottom']
    .map(o => parseInt(style[`margin-${o}`]))
    .reduce((acc, curr) => acc + curr, h)

  return height
}

export const Collapsible = ({
  collapsed,
  className,
  children,
  'data-mu-test': dataAttributeTest
}) => {
  const elRef = useRef(null)
  const elContentRef = useRef(null)

  const prevCollapsed = useRef(collapsed)
  const animation = useRef(null)

  const shrink = () => {
    const startHeight = `${elContentRef.current.offsetHeight}px`

    // If there is already an animation running
    if (animation.current) {
      // Cancel the current animation
      animation.current.cancel()
    }

    elRef.current.style.overflow = 'hidden'

    // Start a WAAPI animation
    animation.current = elRef.current.animate(
      {
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, 0]
      },
      {
        duration,
        easing: 'ease-out',
        fill: 'backwards'
      }
    )

    // When the animation is complete, call onAnimationFinish()
    animation.current.onfinish = () => onAnimationFinish(true)
  }

  const { current: open } = useRef(() => {
    // Apply a fixed height on the element
    elRef.current.style.height = `${getElementHeight(elContentRef.current)}px`
    // Wait for the next frame to call the expand function
    window.requestAnimationFrame(() => expand())
  })

  const { current: expand } = useRef(() => {
    // Calculate the open height of the element (summary height + content height)
    const endHeight = `${getElementHeight(elContentRef.current)}px`

    // If there is already an animation running
    if (animation.current) {
      // Cancel the current animation
      animation.current.cancel()
    }

    elRef.current.style.overflow = 'hidden'

    // Start a WAAPI animation
    animation.current = elRef.current.animate(
      {
        // Set the keyframes from the startHeight to endHeight
        height: [0, endHeight]
      },
      {
        duration,
        easing: 'ease-out',
        fill: 'backwards'
      }
    )
    // When the animation is complete, call onAnimationFinish()
    animation.current.onfinish = () => onAnimationFinish(false)
  })

  const { current: onAnimationFinish } = useRef(newCollapsed => {
    // Clear the stored animation
    animation.current = null

    if (elRef.current && elRef.current.style) {
      // Remove the overflow hidden and the fixed height
      elRef.current.style.height = ''
      elRef.current.style.overflow = ''
    }
  })

  useEffect(() => {
    if (collapsed !== prevCollapsed.current) {
      if (collapsed) {
        shrink()
      } else {
        open()
      }
    }

    prevCollapsed.current = collapsed
  }, [collapsed])

  return (
    <StyledContainer
      className={classnames(
        'e-collapsible',
        {
          'e-collapsible--collapsed': collapsed,
          'e-collapsible--expanded': !collapsed
        },
        className
      )}
      data-mu-test={dataAttributeTest}
      ref={elRef}
      $collapsed={collapsed}
    >
      <StyledInner className='e-collapsible__inner' ref={elContentRef}>
        {children}
      </StyledInner>
    </StyledContainer>
  )
}

Collapsible.propTypes = {
  children: PropTypes.node,
  collapsed: PropTypes.bool,
  className: PropTypes.string,
  'data-mu-test': PropTypes.string
}
